/**
 * Fake dataset for the 通知センター concept screen.
 *
 * SEEDED, not random: a demo whose numbers move every reload cannot be discussed. The generator below is a
 * plain LCG, so the same 168 rows come back in the same order on every load and two people looking at the
 * screen are looking at the same thing.
 *
 * The shapes are copied from what the sync pipeline really produces — clinic codes, 提出年月, aggregate keys,
 * HTTP failures, 返戻/入金 notices — so the columns are being judged against plausible content rather than
 * lorem ipsum. Nothing here talks to a server.
 */
import {
  INBOUND_KIND,
  MASTER_KIND,
  MASTER_KIND_LABEL,
  NOTIFICATION_OPERATION,
  NOTIFICATION_OPERATION_LABEL,
  NOTIFICATION_TAB,
  type InboundKind,
  type MasterKind,
  type NotificationErrorDetail,
  type NotificationItem,
  type NotificationTab,
} from '@/types/admin-notification-center';

/* --------------------------------------------------------------------------------- seeded generator */

let seed = 20260827;
const rand = (): number => {
  // Numerical Recipes LCG. Good enough to scatter demo data, and it never surprises anyone at 3am.
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
};
const pick = <T,>(xs: readonly T[]): T => xs[Math.floor(rand() * xs.length)];
const int = (min: number, max: number): number => min + Math.floor(rand() * (max - min + 1));

/* ------------------------------------------------------------------------------------- vocabularies */

const CLINICS: readonly { number: string; name: string }[] = [
  { number: '980001', name: 'けいろう鍼灸院 新宿' },
  { number: '980002', name: 'けいろう治療院 池袋' },
  { number: '980003', name: 'あおば訪問マッサージ' },
  { number: '980004', name: 'みどり鍼灸マッサージ院' },
  { number: '980005', name: 'さくら在宅リハビリ院' },
  { number: '980006', name: 'ひまわり訪問鍼灸' },
  { number: '980007', name: 'こもれび治療院' },
  { number: '980008', name: 'なごみ鍼灸接骨院' },
  { number: '000431', name: '中央訪問マッサージ' },
  { number: '000440', name: '港北はり灸院' },
];

/**
 * 受診者 names. Forty, so every one of the ten 治療院 can have a roster of its own (see ROSTERS).
 */
const PATIENT_NAMES: readonly string[] = [
  '田中 太郎', '佐藤 花子', '鈴木 一郎', '高橋 美咲',
  '伊藤 健二', '渡辺 幸子', '山本 三郎', '中村 由美',
  '小林 大輔', '加藤 悦子', '吉田 正雄', '山田 千代',
  '佐々木 剛', '松本 里美', '井上 康夫', '木村 敏子',
  '林 秀樹', '清水 和子', '斎藤 学', '山口 節子',
  '渡部 明', '大野 恵', '菊地 誠', '藤田 里子',
  '原田 隆', '石川 京子', '中島 修', '前田 郁子',
  '岡田 拓也', '長谷川 潤', '村上 房子', '近藤 茂',
  '坂本 洋子', '遠藤 宏', '青木 春美', '福田 直樹',
  '西村 好子', '太田 光男', '三浦 育子', '藤井 慎一',
];

/** How many 受診者 each 治療院 gets. 40 names ÷ 10 治療院. */
const PATIENTS_PER_CLINIC = 4;

/**
 * The four 受診者番号 inside one clinic's roster.
 *
 * Every clinic's numbering STARTS AT 1 and climbs, so all four magnitudes are deliberately small and the
 * same across clinics — that is the shape the real column has (rah_db 2026-08-27: 42,850 patients, every
 * clinic's `patients.serial_number` beginning at 1, the longest reaching 11,280).
 */
const SERIAL_STEPS: readonly number[] = [8, 236, 1147, 3074];

/**
 * A 受診者 BELONGS TO ONE 治療院, and carries that clinic's own numbering.
 *
 * This is a roster and not a flat list of people for one reason, and it is the same reason 受診者番号 sits
 * directly beside 治療院ID in the table: the number is scoped PER CLINIC. Drawing a clinic and a patient
 * independently — which is what this file did first — put 「1764 / 松本 里美」 on a row for 000440 and again
 * on a row for 980003, and a reader who sees one number attached to one name at two different clinics comes
 * away believing it is a global patient id. It is not, and the demo must not teach that.
 *
 * The `ci * 13` offset keeps every clinic's numbers distinct from every other's. Two clinics genuinely CAN
 * hold the same 受診者番号 for two different people, but a reviewer meeting that in a screenshot has no way
 * to tell it from a bug, so the demo does not go there.
 */
const ROSTERS: readonly (readonly { serial: number; name: string }[])[] = CLINICS.map((_, ci) =>
  PATIENT_NAMES.slice(ci * PATIENTS_PER_CLINIC, (ci + 1) * PATIENTS_PER_CLINIC).map((name, pi) => ({
    serial: SERIAL_STEPS[pi] + ci * 13,
    name,
  }))
);

const SUBMIT_MONTHS: readonly string[] = ['202605', '202606', '202607', '202608'];

/**
 * 保険者番号 samples taken from the real `insurers` table (rah_db, 2026-08-27) so the column is judged
 * against the shapes it will really hold: plain 8-digit numbers, plus the 公費 form that carries a
 * full-width bracketed suffix and is NOT 8 digits.
 */
/** 保険者 — number AND name, because the マスタ同期 tab now shows the pair for every kind. */
const INSURERS: readonly { number: string; name: string }[] = [
  { number: '06350383', name: '全国健康保険協会 東京支部' },
  { number: '67160150', name: '東京都後期高齢者医療広域連合' },
  { number: '67404046', name: '神奈川県後期高齢者医療広域連合' },
  { number: '00390302', name: '関東ITソフトウェア健康保険組合' },
  { number: '80140081', name: '新宿区国民健康保険' },
  { number: '67070748', name: '埼玉県後期高齢者医療広域連合' },
  { number: '01130012', name: '板橋区国民健康保険' },
  { number: '06139097', name: '全国健康保険協会 神奈川支部' },
  { number: '0034（障0）', name: '東京都心身障害者医療費助成' },
  { number: '0034（親1）', name: '東京都ひとり親家庭等医療費助成' },
];

/* ------------------------------------------------------------------------------ failure vocabularies */

interface FailureTemplate {
  code: string;
  summary: string;
  message: string;
  httpStatus: number | null;
  rows: readonly { field: string; value: string; reason: string }[];
}

const MASTER_FAILURES: readonly FailureTemplate[] = [
  {
    code: 'VALIDATION_ERROR',
    summary: '必須項目が未入力のため、審査システムに登録できませんでした。',
    message: '審査システムの入力チェックで拒否されました。',
    httpStatus: 422,
    rows: [
      { field: '郵便番号', value: '(空)', reason: '必須項目です' },
      { field: '電話番号', value: '03-1234', reason: '桁数が不足しています' },
    ],
  },
  {
    code: 'DUPLICATE_KEY',
    summary: '同じ会員番号が審査システムに既に存在します。',
    message: '会員番号の重複により登録できませんでした。',
    httpStatus: 409,
    rows: [{ field: '会員番号', value: '0000431', reason: '既に登録済みです' }],
  },
  {
    code: 'CONNECTION_TIMEOUT',
    summary: '審査システムへの接続がタイムアウトしました。',
    message: '接続タイムアウト (5秒)。時間をおいて再送信してください。',
    httpStatus: null,
    rows: [],
  },
  {
    code: 'SERVER_ERROR',
    summary: '審査システム側でエラーが発生しました。',
    message: '審査システムが 500 を返しました。',
    httpStatus: 500,
    rows: [{ field: '-', value: '-', reason: 'Internal Server Error' }],
  },
];

const REZEPT_FAILURES: readonly FailureTemplate[] = [
  {
    code: 'CLINIC_NOT_FOUND',
    summary: '審査システムに該当の治療院が登録されていません。',
    message: '治療院マスタを先に同期してから、再送信してください。',
    httpStatus: 404,
    rows: [{ field: '治療院ID', value: '980007', reason: '審査システムに未登録' }],
  },
  {
    code: 'VALIDATION_ERROR',
    summary: '保険情報に不備があるため、送信できませんでした。',
    message: '審査システムの入力チェックで拒否されました。',
    httpStatus: 422,
    rows: [
      { field: '保険者番号', value: '0113', reason: '桁数が不足しています' },
      { field: '被保険者証番号', value: '(空)', reason: '必須項目です' },
      { field: '負担割合', value: '4', reason: '1〜3 の範囲外です' },
    ],
  },
  {
    code: 'CONSENT_MISSING',
    summary: '同意書の有効期間が切れています。',
    message: '施術月が同意書の有効期間外です。同意書を更新してください。',
    httpStatus: 422,
    rows: [
      { field: '同意年月日', value: '2025-11-02', reason: '施術月に対して期限切れ' },
      { field: '施術年月', value: '202607', reason: '同意書の有効期間外' },
    ],
  },
  {
    code: 'CONNECTION_TIMEOUT',
    summary: '審査システムへの接続がタイムアウトしました。',
    message: '接続タイムアウト (5秒)。時間をおいて再送信してください。',
    httpStatus: null,
    rows: [],
  },
];


/** The address master goes as a whole-table file, so its sentences talk about the file, not a record. */

/**
 * One sentence per 種別, written as the thing that HAPPENED to the rezept — not as a system event.
 *
 * There is no failure variant here on purpose (owner's decision): a message only reaches this tab because
 * Shinsa delivered it successfully, so 「結果」 has nothing to distinguish on this tab.
 */
const INBOUND_MESSAGE: Record<InboundKind, string> = {
  [INBOUND_KIND.RETURNED]: '返戻されました。内容をご確認のうえ、再提出年月を登録してください。',
  [INBOUND_KIND.RETURN_CANCELLED]: '返戻が取り消されました。レセプトを確定に戻しました。',
  [INBOUND_KIND.PAYMENT_CANCELLED]: '消込が解除されました。審査済に戻しました。',
  [INBOUND_KIND.PARTIAL_UNPAID]: '審査の結果、一部が不支給となりました。再提出の必要はありません。',
};

/* ------------------------------------------------------------------------------------------ builder */

/** Newest row is 「now」, and rows walk backwards in time from there. Demo data should look alive. */
const BASE_TIME = new Date('2026-08-27T09:40:00+09:00').getTime();
let cursor = 0;

/**
 * Each row is 4–90 minutes older than the one before, so the list reads as a real feed.
 *
 * Seconds are jittered on purpose: 処理時間 shows them, and a page where every send starts exactly on the
 * minute makes the column look like a placeholder rather than a measurement.
 */
const nextTimestamp = (): string => {
  cursor += int(4, 90) * 60_000 + int(0, 59) * 1_000;
  return new Date(BASE_TIME - cursor).toISOString();
};

const errorDetailOf = (t: FailureTemplate, occurredAt: string, endpoint: string): NotificationErrorDetail => ({
  code: t.code,
  message: t.message,
  httpStatus: t.httpStatus,
  endpoint,
  occurredAt,
  rows: t.rows.map(r => ({ ...r })),
});

let nextId = 1000;

const buildMaster = (): NotificationItem => {
  const kind = pick(Object.values(MASTER_KIND)) as MasterKind;
  const startedAt = nextTimestamp();

  // A master push queues exactly like a rezept one, so it can be caught mid-flight too. Rarer here: a
  // single record settles in well under a second, so the window in which it is 送信中 is small.
  const running = rand() < 0.07;
  const failed = !running && rand() < 0.32;
  const occurredAt = startedAt;
  const template = failed ? pick(MASTER_FAILURES) : null;

  // One record, one HTTP call — but the clock starts when the job is DISPATCHED, not when a worker picks
  // it up, and that pickup alone costs about a second on the polling driver (SYNC_QUEUE_SLEEP). So a master
  // push lands 1–4 s after it is queued.
  //
  // The floor of one second is not cosmetic: at 120–900 ms both ends of 処理時間 rounded to the SAME second
  // and the column printed 「令和08年08月27日 06:46:20 ~ 06:46:20」, which reads as a broken cell rather than
  // as a fast send.
  const durationMs = int(1_100, 4_000) + (failed ? int(1, 3) * 1_000 : 0);

  // The business code shown next to the kind, and the aggregate key it belongs to. Both kinds name ONE
  // record and key as 「{code}-{sub_number}」:
  //   治療院  {clinic_number}-{sub_number}
  //   保険者  {insurer_number}-{sub_number}
  const record = kind === MASTER_KIND.CLINIC ? pick(CLINICS) : pick(INSURERS);
  const code = record.number;

  const key = `${code}-${int(1, 3)}`;

  // 対象件数 — how many records this RUN covered. A single save pushes one; a bulk edit or the nightly
  // reconcile sweep pushes a handful.
  const total = rand() < 0.72 ? 1 : int(2, 40);
  const failedCount = failed ? Math.min(total, rand() < 0.8 ? 1 : int(2, 5)) : 0;
  const succeeded = running ? int(0, Math.max(0, total - failedCount)) : total - failedCount;

  return {
    id: nextId++,
    tab: NOTIFICATION_TAB.MASTER,
    records: [],
    recordsTotal: 0,
    isNew: null,
    // No 「press」 on this tab — an observer fires per record, so there is no operation to name.
    operation: null,
    operationLabel: null,
    level: running ? 'info' : failed ? 'error' : 'success',
    read: running ? false : rand() < 0.55,
    occurredAt,
    startedAt,
    finishedAt: running ? null : new Date(new Date(startedAt).getTime() + durationMs).toISOString(),
    targetLabel: MASTER_KIND_LABEL[kind],
    masterKind: kind,
    serviceType: null,
    targetCode: code,
    targetName: record?.name ?? null,
    targetKey: key,
    // 内容 is BUILT BY THE TABLE from level + window + counts (see Summary in NotificationCenterTable).
    // What stays here is only the part no formula can produce: which failure it was. A success row needs
    // no sentence at all, so it carries none rather than a decorative one.
    summary: template ? template.summary : '',
    // NULL on this tab: the マスタ同期 columns read targetCode / targetName, which hold whichever master
    // the run is about. A 保険者 number in a field called clinicNumber would be a lie the API inherits.
    clinicNumber: null,
    clinicName: null,
    patientName: null,
    patientSerial: null,
    submitYearMonth: null,
    inboundKind: null,
    totalCount: total,
    successCount: succeeded,
    failureCount: failedCount,
    outOfScopeCount: 0,
    attempts: failed ? int(1, 4) : 1,
    retryCount: 0,
    sends: [],
    // Nothing to re-push while the queue still has it.
    retryable: failed,
    hasDriftCsv: false,
  errorDetail: template ? errorDetailOf(template, occurredAt, `POST /api/v1/sync/${kind}`) : null,
  };
};

/**
 * ONE ROW = ONE SEND, not one レセプト.
 *
 * A send is a single 確定 press: one clinic × one 提出年月 × one service, covering anywhere from a handful of
 * rezepts to several hundred. The row reports when it started, when it finished, and how many of its
 * rezepts landed.
 *
 * A send that is still in the queue has NO finish time — see `finishedAt` on the view model.
 */
const buildRezept = (): NotificationItem => {
  const clinic = pick(CLINICS);
  const startedAt = nextTimestamp();
  const ym = pick(SUBMIT_MONTHS);
  const service: 'A2' | 'R2' = rand() < 0.7 ? 'A2' : 'R2';

  // Batch sizes copied from what 確定 really produces: mostly a page or two, occasionally a month-end run.
  const total = rand() < 0.25 ? int(120, 500) : int(3, 60);

  // Roughly one send in eight is still running. Rare enough that the tab is not full of them, common enough
  // that the state is visible without hunting.
  const running = rand() < 0.13;

  const failed = running
    ? (rand() < 0.3 ? int(1, 3) : 0)
    : rand() < 0.35
      ? Math.min(total, int(1, Math.max(1, Math.round(total * 0.12))))
      : 0;

  // While running the tallies are PARTIAL — what has come back so far, not the final answer.
  const succeeded = running ? int(0, Math.max(0, total - failed - 1)) : total - failed;

  const template = !running && failed > 0 ? pick(REZEPT_FAILURES) : null;

  // A send takes seconds to minutes depending on its size; finished sends get a plausible duration.
  // Durations taken from this project's own measurements, not invented: the 2026-08-26 benchmark drained
  // 500 rezepts in ~5 s and 4,000 in ~36 s locally (docs/common-sync/REPORT_REDIS_QUEUE_2026-08-26.md), so
  // ~10 ms each with the index in place. Padded to 30–80 ms here for a real network hop, plus a couple of
  // seconds of queue pick-up. A 500-record press then lands at 20–45 s and a 40-record one inside 5 s —
  // which is exactly why 処理時間 shows seconds.
  const durationMs = total * int(30, 80) + int(1, 3) * 1_000;

  const finishedAt = running ? null : new Date(new Date(startedAt).getTime() + durationMs).toISOString();

  // A re-send only exists for a press that finished with failures, and it happens AFTER that press — often
  // much later, which is precisely why it is drawn on its own line instead of inside 同期時間.
  const retried = !running && failed > 0 ? (rand() < 0.4 ? int(1, 2) : 0) : 0;

  // The stack the 内容 cell draws: the press first, then one block per re-send. Each re-send picks up the
  // failures the previous attempt left and — in the sample data — fixes most of them, which is what makes
  // the stack readable as a history rather than as a repeated total.
  const sends: NotificationItem['sends'] = [
    {
      correlationId: `send-${nextId}-0`,
      startedAt,
      finishedAt,
      total,
      success: succeeded,
      failure: failed,
      outOfScope: 0,
      running: running ? total - succeeded - failed : 0,
    },
  ];

  let outstanding = failed;
  for (let attempt = 1; attempt <= retried; attempt++) {
    const at = new Date(new Date(finishedAt ?? startedAt).getTime() + attempt * int(20, 300) * 60_000);
    const landed = attempt === retried ? outstanding : int(0, outstanding);

    sends.push({
      correlationId: `send-${nextId}-${attempt}`,
      startedAt: at.toISOString(),
      finishedAt: new Date(at.getTime() + int(1, 20) * 1_000).toISOString(),
      total: outstanding,
      success: landed,
      failure: outstanding - landed,
      outOfScope: 0,
      running: 0,
    });
    outstanding -= landed;
  }

  return {
    id: nextId++,
    tab: NOTIFICATION_TAB.REZEPT,
    // Sample rows are 確定 presses, which name no rezept — their 内容 is a count.
    records: [],
    recordsTotal: 0,
    isNew: null,
    // Sample rows stay 確定送信: the two rarer operations are visible on the LIVE tab, and salting the
    // sample with them would make the mock disagree with what a reader sees against real data.
    operation: NOTIFICATION_OPERATION.UPSERT,
    operationLabel: NOTIFICATION_OPERATION_LABEL[NOTIFICATION_OPERATION.UPSERT],
    // Any failure at all makes a FINISHED send a 失敗 row: 「498 of 500」 is still a send someone has to
    // finish, and burying that behind a green tag is how the two get forgotten.
    level: running ? 'info' : failed > 0 ? 'error' : 'success',
    read: running ? false : rand() < 0.5,
    occurredAt: startedAt,
    startedAt,
    finishedAt,
    targetLabel: 'レセプト',
    masterKind: null,
    serviceType: service,
    targetCode: null,
    targetName: null,
    // The key of a SEND, not of a record: clinic × 提出年月 × service is exactly what one 確定 press covers.
    targetKey: `${clinic.number}-${ym}-${service}`,
    // Only the part the table cannot derive — see buildMaster.
    summary: template?.summary ?? '',
    clinicNumber: clinic.number,
    clinicName: clinic.name,
    // A send covers many patients, so there is no one 受診者 to name — neither the name nor the number.
    patientName: null,
    patientSerial: null,
    submitYearMonth: ym,
    inboundKind: null,
    totalCount: total,
    successCount: succeeded,
    failureCount: failed,
    outOfScopeCount: 0,
    attempts: failed > 0 ? int(1, 4) : 1,
    // The only tab where 再送信 folds into the row rather than raising its own — so it is the only one
    // whose sample rows carry a stack of sends.
    retryCount: retried,
    sends,
    // Nothing to re-push while the queue is still working on it — pressing 再送信 on a running send would
    // dispatch a second copy of jobs that have not finished failing yet.
    retryable: !running && failed > 0,
    hasDriftCsv: false,
  errorDetail: template ? errorDetailOf(template, startedAt, 'POST /api/v1/sync/rezept') : null,
  };
};

const buildInbound = (): NotificationItem => {
  // Clinic FIRST, then somebody who is actually registered there — never the two drawn independently.
  const clinicIndex = int(0, CLINICS.length - 1);
  const clinic = CLINICS[clinicIndex];
  const patient = pick(ROSTERS[clinicIndex]);
  const kind = pick(Object.values(INBOUND_KIND)) as InboundKind;
  const occurredAt = nextTimestamp();
  const ym = pick(SUBMIT_MONTHS);

  return {
    id: nextId++,
    tab: NOTIFICATION_TAB.INBOUND,
    records: [],
    recordsTotal: 0,
    isNew: null,
    // Inbound messages are Shinsa's, not a RAH press.
    operation: null,
    operationLabel: null,
    // ALWAYS success on this tab. A message is only listed here because Shinsa delivered it, so there is no
    // failed counterpart to show — the 結果 column, the error CSV and the retry button are all dropped for
    // this tab in the table.
    level: 'info',
    read: rand() < 0.45,
    occurredAt,
    startedAt: occurredAt,
    finishedAt: null,
    targetLabel: 'レセプト通知',
    masterKind: null,
    // A 審査 notice is about ONE レセプト, and a レセプト is either A2 or R2 — same 70/30 split as the
    // sending tab so the two read consistently.
    serviceType: rand() < 0.7 ? 'A2' : 'R2',
    targetCode: null,
    targetName: null,
    // Same 受診者番号 as the column, not a fresh random one: the key is meant to be the id of THIS レセプト,
    // and a key that disagrees with the row it sits on is worse than no key at all.
    targetKey: `${clinic.number}-${patient.serial}-${ym}`,
    summary: INBOUND_MESSAGE[kind],
    clinicNumber: clinic.number,
    clinicName: clinic.name,
    patientName: patient.name,
    patientSerial: String(patient.serial),
    submitYearMonth: ym,
    inboundKind: kind,
    totalCount: null,
    successCount: null,
    failureCount: null,
    outOfScopeCount: 0,
    attempts: 1,
    retryCount: 0,
    sends: [],
    retryable: false,
    hasDriftCsv: false,
  errorDetail: null,
  };
};

/**
 * Built once at module load, newest first.
 *
 * Counts are deliberately uneven — 68 / 62 / 38 — so pagination, the badges and the 結果 summary all have
 * something different to say on each tab.
 */
const build = (tab: NotificationTab, n: number): NotificationItem[] => {
  cursor = 0;
  const make = tab === NOTIFICATION_TAB.MASTER ? buildMaster : tab === NOTIFICATION_TAB.REZEPT ? buildRezept : buildInbound;
  return Array.from({ length: n }, make);
};

export const MOCK_NOTIFICATIONS: NotificationItem[] = [
  ...build(NOTIFICATION_TAB.MASTER, 68),
  ...build(NOTIFICATION_TAB.REZEPT, 62),
  ...build(NOTIFICATION_TAB.INBOUND, 38),
];

/** 治療院ID options for the filter — taken from the data itself, so the dropdown can never offer a dead code. */
export const MOCK_CLINIC_OPTIONS = CLINICS.map(c => ({
  value: c.number,
  label: `${c.number} ${c.name}`,
}));
